/* globals zoomSdk */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GlobalThemeProvider, Button, Divider, Dialog } from '@zoom/react-ui';
import Countdown from './Countdown';
import Queue from './Queue';
import SetTimer from './SetTimer';
import {
  BackButton,
  TimeButtons,
  DisplayTimerForOthersButton,
} from './Buttons';
import {
  determineBackgroundColor,
  determineTextColor,
  determineTimerColor,
  formatTime,
  GREEN,
  BLACK,
} from '../utils';
import useSupportRunningContext from '../hooks/useSupportRunningContext';
import useOperationSystem from '../hooks/useOperatingSystem';
import './Home.css';
import useEnterEscKeyPress from '../hooks/useEnterEscKeyPress';
import useDynamicIndicatorClientCheck from '../hooks/useDynamicIndicatorClientCheck';
import useWithSound from '../hooks/useWithSound';
import CustomTooltip from '../utilities/CustomTooltip/CustomTooltip';
import useIsDesktopCheck from '../hooks/useIsDesktopCheck';
import { mobileAudioPlay } from '../utilities/Mobile/playMobileAlarm';
import { SoundButton } from '../utilities/MusicInTimer/SoundButton';
import {
  selectIsTimerAudioOn,
  selectedTimerMusicOption,
} from '../utilities/MusicInTimer/reducer';
import useCountNegativeAfterAlarm from '../hooks/useCountNegativeAfterAlarm';
import useStorePreferredMusic from '../hooks/useStorePreferredMusic';
import { BASE_TIMER_TIME } from '../utilities/Constants';
import DigitalCanvas from '../utilities/DigitalCanvas';
import ToastManager, {
  killActiveToasts,
  alignToasts,
  removeToastStyling,
} from '../utilities/ToastManager/ToastManager';

function Home({
  currentTab,
  timerIsRunning,
  setTimerIsRunning,
  timerVideoToggleOn,
  setTimerVideoToggleOn,
  stopwatchIsOn,
  stopwatchVideoToggleOn,
  dynamicTimerOnEvent,
  dynamicTimerRemoveEvent,
  timerTime,
  setTimerTime,
  startTime,
  setStartTime,
  myDynamicIndicator,
  userContext,
  countdownStatus,
  setCountdownStatus,
  somethingWentWrong,
  setSomethingWentWrong,
  size,
  width,
  timerAudioSettingFromDI,
  isVideoDimensionsChange,
  isTimerDISet,
  isStopwatchDISet,
  setMeetingTimerDisabled,
  meetingTimerDisabled,
  clientSupportNegativeAlarm,
  setClientSupportNegativeAlarm,
  screenName,
  isVideoTurnedOn,
  setResumedTime,
  resumedTime,
  setStartTimestamp,
  startTimestamp,
}) {
  // vfDidDisplayOnVideo is the source of truth if VF is successfully display on the video
  const [vfDidDisplayOnVideo, setVfDidDisplayOnVideo] = useState(false);
  const [openModal, setOpenModal] = useState({ open: false });
  const [displayCustomInput, setDisplayCustomInput] = useState(true); // Can be removed
  const myTimerAudioToggleOn = useSelector(selectIsTimerAudioOn);
  const [countdownKey, setCountdownKey] = useState(0);
  const [timerEnd, setTimerEnd] = useState(false);
  const [presetTime, setPresetTime] = useState(false);
  const toastReset = useRef(null);
  const timerWorker = useRef(null);
  const [timerId, setTimerId] = useState(0);
  const [oldHostSupport, setOldHostSupport] = useState(false);
  const [tempTimerTime, setTempTimerTime] = useState(BASE_TIMER_TIME);
  const [timerQueue, setTimerQueue] = useState([]);
  const [activeToasts, setActiveToasts] = useState([]);

  /**
   * Version and Device Type Control Variables.
   */
  const dynamicIndicatorClientCheck = useDynamicIndicatorClientCheck();
  const supportRunningContext = useSupportRunningContext();
  const isMacOS = useOperationSystem();
  const clientSupportWithSound = useWithSound();
  const isDesktop = useIsDesktopCheck();
  const selectedMusicOption = useSelector(selectedTimerMusicOption);
  const negativeTimerClient = useCountNegativeAfterAlarm();

  const andTheWinnerRef = useRef(null);
  const beethovenRef = useRef(null);
  const goldenRef = useRef(null);
  const ohlalaRef = useRef(null);
  const onTheLaunchpadRef = useRef(null);
  const pencilsDownRef = useRef(null);
  const timesUpRef = useRef(null);
  const vamosRef = useRef(null);
  const vegasRef = useRef(null);
  const weMadeItRef = useRef(null);
  const defaultAlarmRef = useRef(null);

  const refsList = [
    {
      id: 0,
      ref: defaultAlarmRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/defaultAlarm.wav`,
    },
    {
      id: 1,
      ref: timesUpRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/times_up.mp3`,
    },
    {
      id: 2,
      ref: vegasRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/vegas.mp3`,
    },
    {
      id: 3,
      ref: pencilsDownRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/pencils_down.mp3`,
    },
    {
      id: 4,
      ref: andTheWinnerRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/and_the_winner_is.mp3`,
    },
    {
      id: 5,
      ref: beethovenRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/beethoven_begins.mp3`,
    },
    {
      id: 6,
      ref: onTheLaunchpadRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/on_the_launchpad.mp3`,
    },
    {
      id: 7,
      ref: weMadeItRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/we_made_it.mp3`,
    },
    {
      id: 8,
      ref: vamosRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/vamos.mp3`,
    },
    {
      id: 9,
      ref: goldenRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/golden_ticket.mp3`,
    },
    {
      id: 10,
      ref: ohlalaRef,
      src: `${process.env.PUBLIC_URL}/MusicFiles/ohh_lala.mp3`,
    },
  ];

  /**
   * This event puts the minutes input box in focus on app start. Emulating a double click behaviur inside the box.
   */
  window.onanimationend = function () {
    const input = document.getElementById('input-minutes');
    if (input && isMacOS) {
      input.focus();
      input.select();
    }
  };

  /** set preferred music option */
  useStorePreferredMusic();

  /**
   * setTimerTimeWithConstraint function take time as an argument and sets contains on it.
   * The time constraint range is 0 to 360000 seconds or 0 secs to 99 hrs 59 minutes and 59 secs, both in positive and negative.
   * This code sets Time to negative only when client supports negative alarm is not in main client.
   * @type {(function(*): void)|*}
   */
  const setTimerTimeWithConstraint = useCallback(
    (time, setMethod) => {
      if (time <= 0 && time > -360000) {
        clientSupportNegativeAlarm && supportRunningContext
          ? setMethod(time)
          : setMethod(0);
      } else if (time <= -360000) {
        clientSupportNegativeAlarm && supportRunningContext
          ? setMethod(-359999)
          : setMethod(0);
      } else if (time >= 0 && time < 360000) {
        setMethod(time);
      } else {
        setMethod(359999); // Max input: 99 hours, 59 minutes, 59 seconds
      }
    },
    [clientSupportNegativeAlarm]
  );

  const formatTimer = useCallback(
    (time) => {
      let hours = formatTime(time).hours;
      let minutes = formatTime(time).minutes;
      let seconds = formatTime(time).seconds;
      if (time === 0) {
        if (!timerIsRunning) {
          hours = '00';
          minutes = '00';
          seconds = '00';
        }
      }
      return { hours: hours, minutes: minutes, seconds: seconds };
    },
    [
      countdownKey,
      countdownStatus,
      startTime,
      timerIsRunning,
      timerTime,
      setTimerTimeWithConstraint,
      stopwatchIsOn,
      openModal,
      supportRunningContext,
      myTimerAudioToggleOn,
      dynamicIndicatorClientCheck,
    ]
  );

  const formatTimerCanvas = useCallback(
    (time) => {
      if (timerEnd === true) {
        time = 0;
      }
      let hours = formatTime(time).hours;
      let minutes = formatTime(time).minutes;
      let seconds = formatTime(time).seconds;

      return (
        <DigitalCanvas
          hours={hours}
          minutes={minutes}
          seconds={seconds}
          startTime={startTime}
          timerTime={timerTime}
          watchOnVideo={timerVideoToggleOn}
          watchType='timer'
          vfOnVideo={vfDidDisplayOnVideo}
          setVfOnVideo={setVfDidDisplayOnVideo}
          size={size}
          width={width}
          isVideoDimensionsChange={isVideoDimensionsChange}
          isVideoTurnedOn={isVideoTurnedOn}
          activeToasts={activeToasts}
          setActiveToasts={setActiveToasts}
        />
      );
    },
    [
      startTime,
      timerTime,
      timerVideoToggleOn,
      size,
      width,
      isVideoDimensionsChange,
      timerIsRunning,
      isVideoTurnedOn,
    ]
  );

  /**
   * playSound function plays the Timer Alarm audio in loop.
   * @type {(function(): void)|*}
   */
  const playSound = useCallback(() => {
    const selectedMusicId = Number(selectedMusicOption.id);
    const currentSelectedMusicRef =
      refsList[selectedMusicId]?.ref ??
      (selectedMusicId === 0 ? defaultAlarmRef : null);
    if (currentSelectedMusicRef?.current?.src) {
      currentSelectedMusicRef.current.muted = false;
      currentSelectedMusicRef.current.currentTime = 0;
      currentSelectedMusicRef.current.play().catch((error) => {
        console.log(error);
      });
    }
  }, [selectedMusicOption, refsList]);

  /**
   * pauseSound function pauses the Timer Alarm audio.
   * @type {(function(): void)|*}
   */
  // only try to pause music if a music option is selected. -1 is when sound is off.
  const pauseSound = useCallback(() => {
    const selectedMusicId = Number(selectedMusicOption.id);
    if (selectedMusicId !== -1) {
      const currentSelectedMusicRef =
        refsList[selectedMusicId]?.ref ??
        (selectedMusicId === 0 ? defaultAlarmRef : null);
      if (currentSelectedMusicRef?.current?.src) {
        currentSelectedMusicRef.current.pause();
        currentSelectedMusicRef.current.currentTime = 0;
      }
    }
  }, [selectedMusicOption, refsList]);

  /**
   * If a personal Timer is started on new client supporting negative Timer for the first Timer,
   * setClient support negative Alarm true.
   */
  function newClientPersonalTimer() {
    if (!isTimerDISet && negativeTimerClient) {
      setClientSupportNegativeAlarm(true);
    }
  }

  /**
   * This Controller function is designed to control the Start, Resume and Pause Buttons of Timer.
   */
  const timerStartLogic = () => {
    newClientPersonalTimer();
    setOldHostSupport(false); // Temporary variable for old client host subscriber support.
    setTimerEnd(false);
    setStartTimestamp(Date.now());
    /* This state means that the Timer is Off. It is either not started yet or it has ended. */
    if (!countdownStatus && !timerIsRunning) {
      setTimerIsRunning(true); // Start the Timer
      setCountdownStatus(true);
    } else if (countdownStatus && timerIsRunning) {
      setTimerIsRunning(false);
    } else if (countdownStatus && !timerIsRunning) {
      setTimerIsRunning(true);
    }
  };
  let startClicked = false;

  function setDynamicIndicator(time) {
    if (
      supportRunningContext &&
      timerVideoToggleOn &&
      dynamicIndicatorClientCheck === 'dISupportedClient'
    ) {
      const timerColor = determineTimerColor(
        timerTime,
        parseInt(window.localStorage.getItem('LastSetTime')) ||
          parseInt(window.localStorage.getItem('PublisherSetTime'))
      );
      const textColor = determineTextColor(timerTime);
      const backgroundColor = determineBackgroundColor(timerTime);
      if (time || (!countdownStatus && !timerIsRunning && !startClicked)) {
        /* This state means that the Timer is Off. It is either not started yet or it has ended. */
        startClicked = true;
        zoomSdk
          .callZoomApi('setDynamicIndicator', {
            timer: {
              action: 'start',
              direction: 'down',
              start: time ? time * 1000 : timerTime * 1000,
              withSound: myTimerAudioToggleOn,
              showNotification: true,
              ...(selectedMusicOption.id > 0 && {
                songChoice: parseInt(selectedMusicOption.id),
              }),
              countNegativeAfterAlarm: true,
            },
            borderColor: GREEN,
            textColor: textColor,
            backgroundColor: BLACK,
          })
          .catch((error) => {
            if (
              error.toString() ===
              'Error: Meeting dropdown feature is disabled.'
            ) {
              setMeetingTimerDisabled(true);
            } else {
              setSomethingWentWrong(true);
            }
          });
      } else if (countdownStatus && timerIsRunning) {
        /* This state means that the Timer is On and currently running. */
        zoomSdk
          .callZoomApi('setDynamicIndicator', {
            timer: {
              action: 'pause',
              withSound: myTimerAudioToggleOn,
              showNotification: true,
              ...(selectedMusicOption.id > 0 && {
                songChoice: parseInt(selectedMusicOption.id),
              }),
            },
            borderColor: timerColor,
            textColor: textColor,
            backgroundColor: backgroundColor,
          })
          .catch((error) => {
            setSomethingWentWrong(true);
          });
      } else if (countdownStatus && !timerIsRunning) {
        /* This state means that the Timer is On and currently paused. */
        zoomSdk
          .callZoomApi('setDynamicIndicator', {
            timer: {
              action: 'resume',
              withSound: myTimerAudioToggleOn,
              showNotification: true,
              ...(selectedMusicOption.id > 0 && {
                songChoice: parseInt(selectedMusicOption.id),
              }),
              countNegativeAfterAlarm: true,
            },
            borderColor: timerColor,
            textColor: textColor,
            backgroundColor: backgroundColor,
          })
          .catch((error) => {
            setSomethingWentWrong(true);
          });
      }
    }
  }
  const startTimer = (time) => {
    var actualStartTime;
    setStartTimestamp(Date.now());
    if (Number.isInteger(time)) {
      actualStartTime = time;
      window.localStorage.setItem('LastSetTime', actualStartTime.toString());
    } else if (!countdownStatus && !timerIsRunning) {
      actualStartTime = tempTimerTime;
      window.localStorage.setItem('LastSetTime', actualStartTime.toString());
    }
    /* private mode (out of meeting or toggle off) */
    if (!supportRunningContext || !timerVideoToggleOn) {
      if (actualStartTime) {
        setTimerTime(actualStartTime);
        setStartTime(actualStartTime);
        setResumedTime(actualStartTime);
        if (!countdownStatus && !timerIsRunning) {
          timerStartLogic();
        } else {
          setTimerIsRunning(true);
          setCountdownStatus(true);
        }
      } else {
        setResumedTime(timerTime);
        timerStartLogic();
      }
      /* DI mode (in meeting and toggle on) */
    } else {
      if (stopwatchIsOn && stopwatchVideoToggleOn && timerVideoToggleOn) {
        setOpenModal({ open: true, time: actualStartTime });
      } else {
        setDynamicIndicator(actualStartTime);
      }
    }

    /**
     * Use this function call to play audio when on a Mobile device.
     * Regular audio playing operations do not function in Mobile and need a specific user
     * action to trigger Audio.
     * This function ideally should only be used for Mobile devices in out of meeting context,
     * when audio is supported in Dynamic Indicator for Mobile.
     */
    if (!isDesktop && myTimerAudioToggleOn) {
      mobileAudioPlay({
        countdownStatus,
        timerIsRunning,
        timerTime,
        timerId,
        setTimerId,
        defaultAlarmRef,
      });
    }
  };

  function resetTimerLogic() {
    setOldHostSupport(false); //Temporary support.
    setPresetTime(false);
    if (toastReset.current !== null) {
      clearTimeout(toastReset.current);
    }
    if (zoomSdk && timerVideoToggleOn && vfDidDisplayOnVideo) {
      zoomSdk.callZoomApi('removeVirtualForeground').catch((error) => {
        /*Do Nothing. Currently, as of June 7, 2023, the foreground APIs do it function but still throws an error saying something went wrong.
        Issue with APIs/Client team for fix. An error dialog box can be put in the future here.*/
      });
      setVfDidDisplayOnVideo(false);
    }
    pauseSound();
    setTimerIsRunning(false);
    setCountdownStatus(false);
    const localStorageTime = parseInt(
      window.localStorage.getItem('LastSetTime')
    );
    const lastSetTime =
      localStorageTime === null || isNaN(localStorageTime)
        ? startTime
        : localStorageTime;
    setTimerTimeWithConstraint(lastSetTime, setTempTimerTime);
    setDisplayCustomInput(true);
  }
  let cancelClicked = false;
  const resetTimer = useCallback(() => {
    if (isTimerDISet && !cancelClicked) {
      zoomSdk.callZoomApi('removeDynamicIndicator').catch((error) => {
        setSomethingWentWrong(true);
      });
      cancelClicked = true;
    } else {
      resetTimerLogic();
    }
  }, [
    timerVideoToggleOn,
    pauseSound,
    dynamicIndicatorClientCheck,
    setTimerTimeWithConstraint,
    supportRunningContext,
    vfDidDisplayOnVideo,
    isTimerDISet,
  ]);

  // a custom hook to listen to 'enter' and 'escape' key press
  useEnterEscKeyPress({
    isCurrentTab: currentTab === 'home',
    openModal,
    startHandler: startTimer,
    onContinueModal,
    isDynamicIndicatorSet: isTimerDISet,
    myDynamicIndicator,
    onCancelModal,
    resetWatch: resetTimer,
    timerTime: timerTime,
  });
  /**
   * This Effect runs when dynamicTimerRemoveEvent is true.
   * i.e. when an onRemoveDynamicIndicator event is received by the App.
   * It effectively resets the timer and removes the Virtual foreground.
   */
  useEffect(() => {
    if (dynamicTimerRemoveEvent === true) {
      if (timerVideoToggleOn || !myDynamicIndicator) {
        resetTimerLogic();
      }
    }
  }, [dynamicTimerRemoveEvent]);
  useEffect(() => {
    return () => {
      resetTimerLogic();
      zoomSdk.callZoomApi('removeVirtualForeground').catch((error) => {
        /*Do Nothing. Currently, as of June 7, 2023, the foreground APIs work but still throws an error saying something went wrong.
        Issue with APIs/Client team for fix. An error dialog box can be put in the future here.*/
      });
    };
  }, []);

  /**
   * This Effect runs when dynamicTimerOnEvent is true.
   * i.e. when an onSetDynamicIndicator event is received by the App, or when the app is started or refreshed.
   */
  useEffect(() => {
    if (dynamicTimerOnEvent === true || dynamicTimerOnEvent === false) {
      setResumedTime(timerTime);
      timerStartLogic();
    }
  }, [dynamicTimerOnEvent]);

  /*
   * This effect implements the keyboard key '1' effect.
   * The timer container changes to 1 minute after pressing key '1'.
   */
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (
        event.key === '1' &&
        document.activeElement.id !== 'input-seconds' &&
        document.activeElement.id !== 'input-minutes' &&
        document.activeElement.id !== 'input-hours'
      ) {
        event.preventDefault();
        setTempTimerTime(60);
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  useEffect(() => {
    if (
      (timerIsRunning || countdownStatus) &&
      !timerVideoToggleOn &&
      vfDidDisplayOnVideo
    ) {
      zoomSdk.callZoomApi('removeVirtualForeground').catch((error) => {
        /*Do Nothing. Currently, as of June 7, 2023, the foreground APIs work but still throws an error saying something went wrong.
        Issue with APIs/Client team for fix. An error dialog box can be put in the future here.*/
      });
      setVfDidDisplayOnVideo(false);
    }
  }, [timerVideoToggleOn]);

  useEffect(() => {
    if (
      ((clientSupportNegativeAlarm && supportRunningContext) ||
        timerTime > 0) &&
      timerIsRunning
    ) {
      if (timerWorker.current === null) {
        let TimerWorker = process.env.PUBLIC_URL + '/TimerWorker.js';
        timerWorker.current = new Worker(TimerWorker);
        timerWorker.current.postMessage({
          startTime: startTime,
          startTimestamp: startTimestamp,
          resumedTime: resumedTime,
          timerIsRunning: timerIsRunning,
        });
        timerWorker.current.onmessage = (e) => {
          setTimerTimeWithConstraint(e.data, setTimerTime);
        };
      }
    }
    return () => {
      if (timerWorker.current) {
        timerWorker.current.terminate();
        timerWorker.current = null;
      }
    };
  }, [
    startTime,
    timerTime,
    startTimestamp,
    resumedTime,
    timerIsRunning,
    setTimerTimeWithConstraint,
  ]);

  /**
   * This is a temporary useEffect to cover one specific flaw in Stop Negative Timer Versioning.
   * The stop button does not show in the case when participant publisher supports negative timer
   * and host subscriber is on old client that does not support negative timer.
   * This effect will help show that button. The Timeout of 5.3 seconds is,
   * because you don't want the stop button to show when both publisher & subscriber are on an old client.
   * This delay will allow the timer to close.
   */
  useEffect(() => {
    if (
      timerTime === 0 &&
      countdownStatus &&
      supportRunningContext &&
      !clientSupportNegativeAlarm &&
      isTimerDISet
    ) {
      if (userContext.role === 'host') {
        setTimeout(() => setOldHostSupport(true), 6100);
      }
    }
  }, [timerTime, countdownStatus, userContext, supportRunningContext]);

  useEffect(() => {
    if (timerTime === 0 && (timerIsRunning || countdownStatus)) {
      // Used to stop the Timer at 0 and end negative counter in old client or out in Main client.
      if (!clientSupportNegativeAlarm || !supportRunningContext) {
        setTimerEnd(true);
      }
      // First condition: if client doesn't support withSound, let timer app play the audio.
      /* Second condition: if client support withSound and "Show Timer to all" is toggled off and DI not set, let timer app play the audio. 
         This condition can handle bug: on withSound supported client, if subscriber turns off "Show timer to all" before someone else sets DI,
         the participant can hear audio twice. One from the client and the other from timer app. So, we cannot just rely timerVideoToggleOn state
         to determine if we have to let timer app play audio when a personal timer is running. 
      */
      if (
        isDesktop &&
        (Number(selectedMusicOption.id) === 0 ||
          !clientSupportWithSound ||
          !supportRunningContext ||
          (clientSupportWithSound && !timerVideoToggleOn && !isTimerDISet))
      ) {
        myTimerAudioToggleOn ? playSound() : pauseSound();
        setTimeout(() => pauseSound(), 6000);
      }

      //When inMain client or old client,reset Timer.
      if (
        (!clientSupportNegativeAlarm && myDynamicIndicator) ||
        !supportRunningContext ||
        (!isTimerDISet && !clientSupportNegativeAlarm)
      ) {
        setTimeout(() => resetTimer(), 6000);
      }
    }
  }, [
    timerTime,
    timerIsRunning,
    countdownStatus,
    myTimerAudioToggleOn,
    playSound,
    pauseSound,
    clientSupportWithSound,
    timerVideoToggleOn,
    selectedMusicOption,
  ]);

  /**
   * This effect runs for Timer Subscriber only, in the case when a personal timer is running and a participant
   * publishes a Dynamic Indicator.
   */
  useEffect(() => {
    if ((isTimerDISet || isStopwatchDISet) && !myDynamicIndicator) {
      resetTimerLogic();
    }
  }, [isTimerDISet, isStopwatchDISet, myDynamicIndicator]);

  async function onContinueModal() {
    try {
      if (isStopwatchDISet && myDynamicIndicator) {
        await zoomSdk.callZoomApi('removeDynamicIndicator');
        if (openModal.timerData !== undefined) {
          setTimerQueue((queue) =>
            queue.filter((item) => item !== openModal.timerData)
          );
          startTimer(openModal.time);
        }
        setTimeout(() => setDynamicIndicator(openModal.time), 500);
      } else {
        setDynamicIndicator();
      }
    } catch (error) {
      console.error('Error removing dynamic indicator:', error);
    } finally {
      setOpenModal({ open: false });
    }
  }

  function onCancelModal() {
    setOpenModal({ open: false });
    setPresetTime(false);
  }

  const activeTimerRunningError = useMemo(
    () =>
      `${screenName ? screenName + "'s " : 'An active '} timer is running. ${
        userContext?.role === 'host' ? 'Some c' : 'C'
      }ontrols are disabled.`,
    [screenName, userContext]
  );

  const toastsData = useMemo(
    () => [
      {
        toastId: 'timesUp',
        type: 'success',
        condition: countdownStatus && timerIsRunning && timerTime <= 0,
        content: "Time's Up!",
        className: 'notification_toast_container toast-times-up',
        closable: true,
        duration: 5000,
        onClose: () => {
          removeToastStyling();
        },
      },
      {
        toastId: 'meetingTimerDisabled',
        type: 'error',
        condition: meetingTimerDisabled,
        content: 'Meeting Timers have been disabled by the Host',
        className: 'notification_toast_container toast-video-error',
        closable: true,
        duration: 5000,
      },
      {
        toastId: 'activeTimerRunningError',
        type: 'info',
        condition: isTimerDISet && !myDynamicIndicator,
        content: activeTimerRunningError,
        className: 'notification_toast_container toast-video-error',
        closable: false,
        duration: 0,
      },
    ],
    [
      countdownStatus,
      timerIsRunning,
      timerTime,
      meetingTimerDisabled,
      isTimerDISet,
      myDynamicIndicator,
      userContext,
    ]
  );

  useEffect(() => {
    if (!countdownStatus && !timerIsRunning) {
      if (activeToasts.includes('timesUp')) {
        setActiveToasts((prevToasts) =>
          prevToasts.filter((toast) => toast !== 'timesUp')
        );
      }
    }
  }, [countdownStatus, timerIsRunning, activeToasts]);

  useEffect(() => {
    if (
      ((!countdownStatus && !timerIsRunning) || !timerVideoToggleOn) &&
      activeToasts.length > 0
    ) {
      killActiveToasts({ activeToasts, setActiveToasts });
    }
  }, [countdownStatus, timerIsRunning, timerVideoToggleOn, activeToasts]);

  useEffect(() => {
    if (activeToasts.length > 1) {
      const toastTimeout = setTimeout(() => {
        alignToasts({ activeToasts });
      }, 300);

      return () => {
        clearTimeout(toastTimeout);
      };
    }
  }, [activeToasts]);

  return (
    <GlobalThemeProvider defaultMode={'dark'}>
      {timerVideoToggleOn &&
        supportRunningContext &&
        countdownStatus &&
        formatTimerCanvas(timerTime)}
      <div
        className='Main__container'
        style={{
          visibility: currentTab === 'home' ? 'inherit' : 'hidden',
        }}
      >
        {meetingTimerDisabled && (
          <ToastManager
            toastsData={toastsData}
            dependencies={[meetingTimerDisabled]}
            activeToasts={activeToasts}
            setActiveToasts={setActiveToasts}
          />
        )}
        {isTimerDISet && !myDynamicIndicator && (
          <ToastManager
            toastsData={toastsData}
            dependencies={[isTimerDISet, myDynamicIndicator]}
            activeToasts={activeToasts}
            setActiveToasts={setActiveToasts}
          />
        )}
        {countdownStatus && timerIsRunning && timerTime <= 0 && (
          <ToastManager
            toastsData={toastsData}
            dependencies={[countdownStatus, timerIsRunning, timerTime]}
            activeToasts={activeToasts}
            setActiveToasts={setActiveToasts}
          />
        )}
        <Dialog
          open={openModal.open}
          title={'Begin Timer for Everyone'}
          closable
          okText={'Yes, continue'}
          onOk={() => {
            onContinueModal();
          }}
          onCancel={() => setOpenModal({ open: false })}
          onEscapeKeyDown={() => setOpenModal({ open: false })}
        >
          You're about to show your timer to everyone. It will replace the
          active stopwatch.
        </Dialog>
        {!countdownStatus ? (
          !timerQueue.length ? (
            <div className='Home__set_timer_container'>
              <div
                data-testid='timerContainer'
                className={`Home__timer_input_container${
                  (!clientSupportNegativeAlarm || !supportRunningContext) &&
                  timerTime === 0 &&
                  timerIsRunning
                    ? ' fade'
                    : ''
                }`}
              >
                <SetTimer
                  hours={formatTimer(tempTimerTime).hours}
                  minutes={formatTimer(tempTimerTime).minutes}
                  seconds={formatTimer(tempTimerTime).seconds}
                  timerTime={timerTime}
                  setTimerTime={setTimerTime}
                  setTimerTimeWithConstraint={setTimerTimeWithConstraint}
                  startTimerOnEnterPress={startTimer}
                  stopwatchIsOn={stopwatchIsOn}
                  openModal={openModal}
                  onContinueModal={onContinueModal}
                  supportRunningContext={supportRunningContext}
                  timerVideoToggleOn={myTimerAudioToggleOn}
                  dynamicIndicatorClientCheck={dynamicIndicatorClientCheck}
                  tempTimerTime={tempTimerTime}
                  setTempTimerTime={setTempTimerTime}
                />
              </div>
              <div className='Home__timer_controls'>
                <div className='Home__start_button'>
                  <Button
                    size='large'
                    fullWidth={true}
                    id='input_start_button'
                    data-tooltip-id='timer-startbutton-tooltip'
                    data-tooltip-delay-show={300}
                    disabled={tempTimerTime === 0}
                    onClick={startTimer}
                  >
                    Start
                  </Button>
                  <CustomTooltip
                    text='Keyboard shortcut'
                    keyName={isMacOS ? 'return' : 'enter'}
                    id='timer-startbutton-tooltip'
                  />
                </div>
                <TimeButtons
                  setStartTime={setStartTime}
                  setPresetTime={setPresetTime}
                  setTimerTimeWithConstraint={setTimerTimeWithConstraint}
                  setTimerTime={setTimerTime}
                  setTempTimerTime={setTempTimerTime}
                  startTimer={startTimer}
                />
                <SoundButton refs={refsList} source='Home' />
              </div>
            </div>
          ) : (
            <Queue
              countdownKey={countdownKey}
              countdownStatus={countdownStatus}
              startTime={startTime}
              timerIsRunning={timerIsRunning}
              timerTime={timerTime}
              setTimerTimeWithConstraint={setTimerTimeWithConstraint}
              stopwatchIsOn={stopwatchIsOn}
              openModal={openModal}
              setOpenModal={setOpenModal}
              supportRunningContext={supportRunningContext}
              myTimerAudioToggleOn={myTimerAudioToggleOn}
              dynamicIndicatorClientCheck={dynamicIndicatorClientCheck}
              clientSupportNegativeAlarm={clientSupportNegativeAlarm}
              setTimerTime={setTimerTime}
              startTimer={startTimer}
              onContinueModal={onContinueModal}
              isMacOS={isMacOS}
              setStartTime={setStartTime}
              setPresetTime={setPresetTime}
              refsList={refsList}
              oldHostSupport={oldHostSupport}
              timerQueue={timerQueue}
              setTimerQueue={setTimerQueue}
              setResumedTime={setResumedTime}
              setStartTimestamp={setStartTimestamp}
              isTimerDISet={isTimerDISet}
              myDynamicIndicator={myDynamicIndicator}
              isStopwatchDISet={isStopwatchDISet}
              stopwatchVideoToggleOn={stopwatchVideoToggleOn}
              timerVideoToggleOn={timerVideoToggleOn}
            />
          )
        ) : (
          <>
            <Countdown
              hours={formatTimer(timerTime).hours}
              minutes={formatTimer(timerTime).minutes}
              seconds={formatTimer(timerTime).seconds}
              countdownStatus={countdownStatus}
              timerTime={timerTime}
              myDynamicIndicator={myDynamicIndicator}
              isTimerDISet={isTimerDISet}
              resetTimer={resetTimer}
              startTimer={startTimer}
              userContext={userContext}
              isMacOS={isMacOS}
              timerIsRunning={timerIsRunning}
              setTimerTime={setTimerTime}
              startTime={startTime}
              supportRunningContext={supportRunningContext}
              myTimerAudioToggleOn={myTimerAudioToggleOn}
              setResumedTime={setResumedTime}
              setStartTime={setStartTime}
              setStartTimestamp={setStartTimestamp}
              timerStartLogic={timerStartLogic}
              timerVideoToggleOn={timerVideoToggleOn}
              isStopwatchDISet={isStopwatchDISet}
              setSomethingWentWrong={setSomethingWentWrong}
            />
            <Queue
              countdownKey={countdownKey}
              countdownStatus={countdownStatus}
              startTime={startTime}
              timerIsRunning={timerIsRunning}
              timerTime={timerTime}
              setTimerTimeWithConstraint={setTimerTimeWithConstraint}
              stopwatchIsOn={stopwatchIsOn}
              openModal={openModal}
              setOpenModal={setOpenModal}
              supportRunningContext={supportRunningContext}
              myTimerAudioToggleOn={myTimerAudioToggleOn}
              dynamicIndicatorClientCheck={dynamicIndicatorClientCheck}
              clientSupportNegativeAlarm={clientSupportNegativeAlarm}
              setTimerTime={setTimerTime}
              startTimer={startTimer}
              onContinueModal={onContinueModal}
              isMacOS={isMacOS}
              setStartTime={setStartTime}
              setPresetTime={setPresetTime}
              refsList={refsList}
              oldHostSupport={oldHostSupport}
              timerQueue={timerQueue}
              setTimerQueue={setTimerQueue}
              setResumedTime={setResumedTime}
              setStartTimestamp={setStartTimestamp}
              isTimerDISet={isTimerDISet}
              myDynamicIndicator={myDynamicIndicator}
              isStopwatchDISet={isStopwatchDISet}
              stopwatchVideoToggleOn={stopwatchVideoToggleOn}
              timerVideoToggleOn={timerVideoToggleOn}
            />
          </>
        )}

        {supportRunningContext && !countdownStatus && (
          <div className='footer'>
            <Divider />
            <DisplayTimerForOthersButton
              displayOnVideo={timerVideoToggleOn}
              onClickFunction={() => {
                setTimerVideoToggleOn(!timerVideoToggleOn);
              }}
              text='Show timer to all'
              // disable toggle when timer is running or in pause state.
              // disabled={countdownStatus}
            />
          </div>
        )}
      </div>

      {displayCustomInput && !countdownStatus && (
        <BackButton onClickFunction={resetTimer} />
      )}
      {refsList.map((music) => {
        return <audio src={music.src} ref={music.ref} key={music.id} />;
      })}
    </GlobalThemeProvider>
  );
}

export default Home;
